import TileLayer from 'ol/layer/Tile'
import XYZ from 'ol/source/XYZ'
import { GOOGLE_MAPS_API_KEY } from '../config'

export type GoogleMapType = 'roadmap' | 'satellite' | 'terrain'

interface SessionEntry {
  session: string
  /** Unix timestamp in ms — token is valid until this time minus a 1h safety margin. */
  expiryMs: number
}

const sessionCache = new Map<GoogleMapType, SessionEntry>()
const pendingFetch = new Map<GoogleMapType, Promise<string>>()

async function fetchSessionToken(mapType: GoogleMapType): Promise<string> {
  const cached = sessionCache.get(mapType)
  if (cached && Date.now() < cached.expiryMs - 3_600_000) return cached.session

  const inflight = pendingFetch.get(mapType)
  if (inflight) return inflight

  const promise = (async (): Promise<string> => {
    const res = await fetch(
      `https://tile.googleapis.com/v1/createSession?key=${GOOGLE_MAPS_API_KEY}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mapType,
          language: 'fr',
          region: 'FR',
          // terrain requires layerRoadmap to render the road overlay
          ...(mapType === 'terrain' ? { layerTypes: ['layerRoadmap'] } : {}),
        }),
      },
    )
    if (!res.ok) throw new Error(`Google Maps session failed: ${res.status}`)
    const data = (await res.json()) as { session: string; expiry: string }
    // Google returns expiry as a Unix timestamp in seconds
    const expiryMs = parseInt(data.expiry, 10) * 1000
    sessionCache.set(mapType, { session: data.session, expiryMs })
    pendingFetch.delete(mapType)
    return data.session
  })()

  pendingFetch.set(mapType, promise)
  return promise
}

/**
 * Creates an OpenLayers TileLayer for a Google basemap type.
 * The session token is fetched lazily on first visibility — no API call at startup.
 * On token error, `initialized` resets so the next selection retries automatically.
 */
export function createGoogleTileLayer(mapType: GoogleMapType): TileLayer<XYZ> {
  const source = new XYZ({
    // Static fallback attribution; Sprint 2 adds dynamic viewport attribution.
    attributions: '© <a href="https://maps.google.com" target="_blank">Google</a>',
    maxZoom: 22,
    crossOrigin: 'anonymous',
  })
  const layer = new TileLayer({ source, zIndex: 0, visible: false })

  let initialized = false
  layer.on('change:visible', () => {
    if (!layer.getVisible() || initialized) return
    initialized = true
    void fetchSessionToken(mapType)
      .then((session) => {
        source.setUrl(
          `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${session}&key=${GOOGLE_MAPS_API_KEY}`,
        )
      })
      .catch((err: unknown) => {
        console.error('Google Maps session error:', err)
        initialized = false // allow retry on next selection
      })
  })

  return layer
}
